import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import { intervalToDuration } from 'date-fns';
import {
  Account,
  ChangeLog,
  Pagination,
  Response,
  AccountingJobProps as AccountingJob,
  AccountingJobStateEnum,
} from '@ui/src';
import { Category } from '@ui/src/components/accounts-selector/types';
import { accountingManagmentApi } from './service';

import ROUTES from '../../../routes';

export interface AccountingManagmentState {
  accountingJobs?: AccountingJob[];
  accountingJob?: AccountingJob;
  pagination?: Pagination;
  changeLogs?: ChangeLog[];
  changeLogsPagination?: Pagination;
  takeCount: number;
  accountCategories?: Category[];
  accountingJobAccounts?: Account[];
  accountingJobAccountsPagination?: Pagination;
}

const initialState: AccountingManagmentState = {
  takeCount: 0,
};

export const accountingManagmentSlice = createSlice({
  name: 'accountingManagment',
  initialState,
  reducers: {
    setTakeCount: (state, action: PayloadAction<number>) => {
      state.takeCount = action.payload;
    },
    clearAccountingJobAccountsList: (state) => {
      state.accountingJobAccounts = undefined;
      state.accountingJobAccountsPagination = undefined;
    },
    clearAccountingManagmentState: (state) => {
      state.accountingJobs = undefined;
      state.pagination = undefined;
    },
    clearAccountingManagmentPanel: (state) => {
      state.accountingJob = undefined;
      state.changeLogsPagination = undefined;
      state.changeLogs = undefined;
      state.accountingJobAccounts = undefined;
      state.accountingJobAccountsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      accountingManagmentApi.endpoints.getAccountingJobs.matchFulfilled,
      (state, action: PayloadAction<Response<AccountingJob>>) => {
        state.accountingJobs = action?.payload?.data?.edges?.map((item) => {
          const result = { ...item.node };

          let durationValue = '0 min';
          let duration: any;

          result.url = generatePath(ROUTES.REPORTS_ACCOUNTING_MANAGMENT_ITEM, {
            itemId: item.node.id,
          });

          result.progress = '-';
          result.fullCurrentDate = '-';

          if (
            result.startedAt &&
            result.state !== AccountingJobStateEnum.IN_PROGRESS &&
            (result.finishedAt || result.cancelledAt)
          ) {
            if (result.finishedAt) {
              duration = intervalToDuration({
                start: 0,
                end: Math.abs(
                  new Date(result.finishedAt).getTime() -
                    new Date(result.startedAt).getTime(),
                ),
              });
            } else if (result.cancelledAt) {
              duration = intervalToDuration({
                start: 0,
                end: Math.abs(
                  new Date(result.cancelledAt).getTime() -
                    new Date(result.startedAt).getTime(),
                ),
              });
            }
          } else if (
            result.startedAt &&
            result.state === AccountingJobStateEnum.IN_PROGRESS
          ) {
            duration = intervalToDuration({
              start: 0,
              end: Math.abs(
                new Date().getTime() - new Date(result.startedAt).getTime(),
              ),
            });
          }

          if (duration && duration.minutes) {
            durationValue = `${
              duration.hours && duration?.hours > 0 ? `${duration.hours} h` : ''
            } ${duration.minutes} min`;
          }

          result.duration = durationValue;

          return result;
        });

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.getAccountingJobItem.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountingJob }>) => {
        let durationValue = '0 min';
        let duration: any;

        if (
          action?.payload?.data?.startedAt &&
          action?.payload?.data?.state !== AccountingJobStateEnum.IN_PROGRESS &&
          (action?.payload?.data?.finishedAt ||
            action?.payload?.data?.cancelledAt)
        ) {
          if (action?.payload?.data?.finishedAt) {
            duration = intervalToDuration({
              start: 0,
              end: Math.abs(
                new Date(action?.payload?.data?.finishedAt).getTime() -
                  new Date(action?.payload?.data?.startedAt).getTime(),
              ),
            });
          } else if (action?.payload?.data?.cancelledAt) {
            duration = intervalToDuration({
              start: 0,
              end: Math.abs(
                new Date(action?.payload?.data?.cancelledAt).getTime() -
                  new Date(action?.payload?.data?.startedAt).getTime(),
              ),
            });
          }
        } else if (
          action?.payload?.data?.startedAt &&
          action?.payload?.data?.state === AccountingJobStateEnum.IN_PROGRESS
        ) {
          duration = intervalToDuration({
            start: 0,
            end: Math.abs(
              new Date().getTime() -
                new Date(action?.payload?.data?.startedAt).getTime(),
            ),
          });
        }

        if (duration && duration.minutes) {
          durationValue = `${
            duration.hours && duration?.hours > 0 ? `${duration.hours} h` : ''
          } ${duration.minutes} min`;
        }

        state.accountingJob = {
          ...action?.payload?.data,
          duration: durationValue,
        };
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.getAccountingManagmentAccountsCategories
        .matchFulfilled,
      (state, action: PayloadAction<Response<Category>>) => {
        state.accountCategories = action.payload.data.edges.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.getAccountingManagmentJobAccounts
        .matchFulfilled,
      (state, action: PayloadAction<Response<Account>>) => {
        state.accountingJobAccounts = action.payload.data.edges.map((item) => ({
          ...item.node,
          search: item.node.description,
        }));

        state.accountingJobAccountsPagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.updateAccountingJob.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountingJob }>) => {
        state.accountingJobs = state?.accountingJobs?.map((item) => {
          let result = { ...item };

          if (result.id === action?.payload?.data?.id) {
            result = {
              ...result,
              ...action.payload?.data,
            };
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.startAccountingJob.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountingJob }>) => {
        state.accountingJobs = state?.accountingJobs?.map((item) => {
          let result = { ...item };

          if (result.id === action?.payload?.data?.id) {
            result = {
              ...result,
              ...action?.payload?.data,
              state: AccountingJobStateEnum.IN_PROGRESS,
            };
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.cancelAccountingJob.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountingJob }>) => {
        state.accountingJobs = state?.accountingJobs?.map((item) => {
          let result = { ...item };

          if (result.id === action?.payload?.data?.id) {
            result = {
              ...result,
              ...action?.payload?.data,
              state: AccountingJobStateEnum.CANCELLED,
            };
          }

          return result;
        });
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.retryAccountingJobItem.matchFulfilled,
      (state, action: PayloadAction<{ data: AccountingJob }>) => {
        const { data } = action.payload;

        const nextAccountingJob = { ...state.accountingJob };

        if (data.state !== nextAccountingJob.state) {
          nextAccountingJob.state = data.state;
        }

        if (data.startedAt !== nextAccountingJob.startedAt) {
          nextAccountingJob.startedAt = data.startedAt;
        }

        if (data.finishedAt !== nextAccountingJob.finishedAt) {
          nextAccountingJob.finishedAt = data.finishedAt;
        }

        if (data.cancelledAt !== nextAccountingJob.cancelledAt) {
          nextAccountingJob.cancelledAt = data.cancelledAt;
        }

        if (data.periodTo !== nextAccountingJob.periodTo) {
          nextAccountingJob.periodTo = data.periodTo;
        }

        if (
          data.balancesLogsCurrentDate !==
          nextAccountingJob.balancesLogsCurrentDate
        ) {
          nextAccountingJob.balancesLogsCurrentDate =
            data.balancesLogsCurrentDate;
        }

        if (
          data.balanceSheetCurrentDate !==
          nextAccountingJob.balanceSheetCurrentDate
        ) {
          nextAccountingJob.balanceSheetCurrentDate =
            data.balanceSheetCurrentDate;
        }

        if (
          data.revaluationCurrentDate !==
          nextAccountingJob.revaluationCurrentDate
        ) {
          nextAccountingJob.revaluationCurrentDate =
            data.revaluationCurrentDate;
        }

        if (
          data.postProcessingCurrentDate !==
          nextAccountingJob.postProcessingCurrentDate
        ) {
          nextAccountingJob.postProcessingCurrentDate =
            data.postProcessingCurrentDate;
        }

        if (
          data.state === AccountingJobStateEnum.IN_PROGRESS &&
          data.startedAt
        ) {
          const duration: any = intervalToDuration({
            start: 0,
            end: Math.abs(
              new Date().getTime() - new Date(data.startedAt).getTime(),
            ),
          });

          if (duration && duration.minutes) {
            const durationValue = `${
              duration.hours && duration.hours > 0 ? `${duration.hours} h` : ''
            } ${duration.minutes} min`;

            if (durationValue !== nextAccountingJob.duration) {
              nextAccountingJob.duration = durationValue;
            }
          } else {
            nextAccountingJob.duration = '0 min';
          }
        }

        if (!isEqual(nextAccountingJob, state.accountingJob)) {
          state.accountingJob = nextAccountingJob as AccountingJob;
        }
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.getAccountingJobsStates.matchFulfilled,
      (state, action: PayloadAction<Response<AccountingJob>>) => {
        const { edges } = action.payload.data;

        const nextAccountingJobs = state?.accountingJobs?.map((item) => {
          const result = { ...item };

          const currentAccountingJobState = edges.find(
            (job) => job.node.id === result.id,
          );

          if (currentAccountingJobState) {
            if (currentAccountingJobState.node.state !== result.state) {
              result.state = currentAccountingJobState.node.state;
            }

            if (currentAccountingJobState.node.startedAt !== result.startedAt) {
              result.startedAt = currentAccountingJobState.node.startedAt;
            }

            if (
              currentAccountingJobState.node.finishedAt !== result.finishedAt
            ) {
              result.finishedAt = currentAccountingJobState.node.finishedAt;
            }

            if (
              currentAccountingJobState.node.cancelledAt !== result.cancelledAt
            ) {
              result.cancelledAt = currentAccountingJobState.node.cancelledAt;
            }

            if (currentAccountingJobState.node.periodTo !== result.periodTo) {
              result.periodTo = currentAccountingJobState.node.periodTo;
            }

            if (
              currentAccountingJobState.node.balancesLogsCurrentDate !==
              result.balancesLogsCurrentDate
            ) {
              result.balancesLogsCurrentDate =
                currentAccountingJobState.node.balancesLogsCurrentDate;
            }

            if (
              currentAccountingJobState.node.balanceSheetCurrentDate !==
              result.balanceSheetCurrentDate
            ) {
              result.balanceSheetCurrentDate =
                currentAccountingJobState.node.balanceSheetCurrentDate;
            }

            if (
              currentAccountingJobState.node.revaluationCurrentDate !==
              result.revaluationCurrentDate
            ) {
              result.revaluationCurrentDate =
                currentAccountingJobState.node.revaluationCurrentDate;
            }

            if (
              currentAccountingJobState.node.postProcessingCurrentDate !==
              result.postProcessingCurrentDate
            ) {
              result.postProcessingCurrentDate =
                currentAccountingJobState.node.postProcessingCurrentDate;
            }

            if (
              currentAccountingJobState?.node.state ===
                AccountingJobStateEnum.IN_PROGRESS &&
              currentAccountingJobState.node?.startedAt
            ) {
              const duration: any = intervalToDuration({
                start: 0,
                end: Math.abs(
                  new Date().getTime() -
                    new Date(
                      currentAccountingJobState?.node.startedAt,
                    ).getTime(),
                ),
              });

              if (duration && duration.minutes) {
                const durationValue = `${
                  duration.hours && duration.hours > 0
                    ? `${duration.hours} h`
                    : ''
                } ${duration.minutes} min`;

                if (durationValue !== result.duration) {
                  result.duration = durationValue;
                }
              } else {
                result.duration = '0 min';
              }
            }
          }

          return result;
        });

        if (!isEqual(nextAccountingJobs, state.accountingJobs)) {
          state.accountingJobs = nextAccountingJobs;
        }
      },
    );
    builder.addMatcher(
      accountingManagmentApi.endpoints.getAccountingJobsChangeLogs
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<ChangeLog>> & {
          meta: { arg: { originalArgs: string } };
        },
      ) => {
        if (!action.meta.arg.originalArgs.includes('cursor')) {
          state.changeLogs = action.payload.data?.edges?.map(
            (item) => item.node,
          );
        } else {
          const nextChangeLogs = ([] as ChangeLog[]).concat(
            state.changeLogs as ChangeLog[],
          );

          action.payload.data.edges?.forEach((item) => {
            nextChangeLogs.push(item.node);
          });

          state.changeLogs = nextChangeLogs;
        }

        state.changeLogsPagination = action.payload.data?.pageInfo;
      },
    );
  },
});

export const {
  clearAccountingManagmentPanel,
  clearAccountingManagmentState,
  setTakeCount,
  clearAccountingJobAccountsList,
} = accountingManagmentSlice.actions;

export default accountingManagmentSlice.reducer;
